import {
	createHoverActionsContainer,
	LOGO_ACTIONS,
} from "../components/header-components/ActionButtons";
import { HeaderElement } from "../types";

// Logo Component as HeaderElement Object
const logoHeaderElement: HeaderElement = {
	id: "logo-component-1",
	type: "Logo",
	tagName: "div",
	data: {
		// Component settings/configuration
		image: "/path/to/logo.png", // component.settings.image
		size: "120px", // component.settings.size
		alt: "Logo",
		maxWidth: "100%",

		// Component behavior settings
		isSelectable: true,
		isDeletable: true,
		hasHoverActions: true,

		// Event handlers (these would be function references in actual implementation)
		onClick: "handleLogoClick",
		onRemove: "handleLogoRemove",
		onEdit: "handleLogoEdit",
	},
	className: "logo-container",
	style: {
		position: "relative",
		cursor: "pointer",
		padding: "8px",
	},
	props: {
		onClick: "handleClick",
		role: "button",
		tabIndex: 0,
	},
	children: [
		// Logo Image Container
		{
			id: "logo-image-container",
			type: "ImageContainer",
			tagName: "div",
			data: {},
			className: "logo-image-wrapper",
			style: {
				maxWidth: "100%",
				height: "auto",
			},
			children: [
				// Logo Image
				{
					id: "logo-image",
					type: "Image",
					tagName: "img",
					data: {
						src: "/path/to/logo.png",
						alt: "Logo",
					},
					className: "logo-img",
					props: {
						src: "/path/to/logo.png",
						alt: "Logo",
					},
					style: {
						maxWidth: "120px", // getSizeValue result
						height: "auto",
					},
				},
			],
		},

		// Hover Actions Container
		{
			id: "logo-hover-actions",
			type: "HoverActions",
			tagName: "div",
			data: {
				showOnHover: true,
			},
			className: "hover-actions",
			style: {
				position: "absolute",
				top: "0",
				right: "0",
			},
			children: [
				// Edit Button
				{
					id: "logo-edit-button",
					type: "ActionButton",
					tagName: "button",
					data: {
						action: "edit",
						icon: "⚙️",
					},
					className: "action-btn edit-btn",
					props: {
						onClick: "handleEditClick",
					},
					children: [
						{
							id: "edit-icon",
							type: "Icon",
							tagName: "span",
							data: {
								icon: "⚙️",
							},
							className: "btn-icon",
							children: [],
						},
					],
				},

				// Remove Button
				{
					id: "logo-remove-button",
					type: "ActionButton",
					tagName: "button",
					data: {
						action: "remove",
						icon: "🗑️",
					},
					className: "action-btn remove-btn",
					props: {
						onClick: "handleRemoveClick",
					},
					children: [
						{
							id: "remove-icon",
							type: "Icon",
							tagName: "span",
							data: {
								icon: "🗑️",
							},
							className: "btn-icon",
							children: [],
						},
					],
				},
			],
		},
	],

	// CSS styles as string
	css: `
        .logo-container {
            position: relative;
            cursor: pointer;
            padding: 8px;
        }
        
        .logo-container.selected {
            border: 2px dashed #2196f3;
        }
        
        .logo-image-wrapper {
            max-width: 100%;
            height: auto;
        }
        
        .logo-img {
            display: block;
            max-width: 100%;
            height: auto;
        }
        
        .hover-actions {
            position: absolute;
            top: 0;
            right: 0;
            opacity: 0;
            background-color: rgba(0, 0, 0, 0.7);
            padding: 4px;
            border-radius: 4px;
            display: flex;
            transition: opacity 0.2s ease;
        }
        
        .logo-container:hover .hover-actions {
            opacity: 1;
        }
        
        .action-btn {
            padding: 4px 8px;
            margin: 0 2px;
            color: white;
            background: none;
            border: none;
            cursor: pointer;
            border-radius: 2px;
            transition: all 0.2s ease;
        }
        
        .edit-btn:hover {
            color: #64b5f6;
            transform: scale(1.1);
        }
        
        .remove-btn:hover {
            color: #f48fb1;
            transform: scale(1.1);
        }
        
        .btn-icon {
            font-size: 12px;
            display: inline-block;
        }
    `,
};

// Alternative simplified version focusing on just the essential logo element
const simplifiedLogoElement: HeaderElement = {
	id: "logo-simple",
	type: "Logo",
	tagName: "div",
	data: {
		image: "/path/to/logo.png",
		size: "120px",
		alt: "Logo",
		isEditable: true,
		isDeletable: true,
	},
	className: "simple-logo",
	style: {
		padding: "8px",
		position: "relative",
		cursor: "pointer",
	},
	children: [
		{
			id: "logo-img",
			type: "Image",
			tagName: "img",
			data: {
				src: "/path/to/logo.png",
				alt: "Logo",
			},
			className: "logo-image",
			props: {
				src: "/path/to/logo.png",
				alt: "Logo",
			},
			style: {
				maxWidth: "120px",
				height: "auto",
			},
		},
	],
	css: `
        .simple-logo {
            position: relative;
            cursor: pointer;
            padding: 8px;
        }
        
        .simple-logo.selected {
            border: 2px dashed #2196f3;
        }
        
        .logo-image {
            display: block;
            max-width: 100%;
            height: auto;
        }
    `,
};

// Factory function to create logo elements with different configurations
const createLogoElement = (
	config: {
		id: string;
		image: string;
		size: string;
		alt?: string;
		hasHoverActions?: boolean;
	},
	publishMode = false
): HeaderElement => {
	const baseElement: HeaderElement = {
		id: config.id,
		type: "Logo",
		tagName: "div",
		props: { onClick: "handleEditClick" },
		data: {
			image: config.image,
			size: config.size,
			alt: config.alt || "Logo",
			isSelectable: true,
			isDeletable: true,
			hasHoverActions: config.hasHoverActions || false,
		},
		className: "logo-container",
		style: {
			position: "relative",
			cursor: "pointer",
			padding: "8px",
			width: "120px",
			objectFit: "contain",
		},
		children: [
			{
				id: `${config.id}-img`,
				type: "Image",
				tagName: "img",
				data: {
					src: config.image,
					alt: config.alt || "Logo",
				},
				className: "logo-img",
				props: {
					src: config.image,
					alt: config.alt || "Logo",
				},
				style: {
					maxWidth: config.size,
					height: "auto",
				},
			},
			...[
				!publishMode
					? createHoverActionsContainer(config.id, LOGO_ACTIONS)
					: ([] as any),
			],
		],
		css: `
            .logo-container {
                position: relative;
                cursor: pointer;
                padding: 8px;
            }
            
            .logo-container.selected {
                border: 2px dashed #2196f3;
            }
            
            .logo-img {
                display: block;
                max-width: 100%;
                height: auto;
            }
        `,
	};

	return baseElement;
};

export { createLogoElement, logoHeaderElement, simplifiedLogoElement };
